import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, Award, Heart, Users, Clock, Sparkles, ArrowRight, Check } from 'lucide-react';

const reasons = [
  {
    icon: Heart,
    title: 'Compassion-First Approach',
    description: 'Every interaction is guided by genuine care, empathy, and respect for individual needs and preferences.',
  },
  {
    icon: Shield,
    title: 'Trusted & Reliable',
    description: 'Rigorous caregiver screening, ongoing training, and consistent quality assurance you can depend on.',
  },
  {
    icon: Users,
    title: 'Personalized Care Plans',
    description: 'Custom care strategies tailored to each individual\'s unique circumstances, health needs, and lifestyle.',
  },
  {
    icon: Clock,
    title: 'Flexible Scheduling',
    description: 'From a few hours a week to around-the-clock support, we adapt to your family\'s changing needs.',
  },
  {
    icon: Award,
    title: 'Experienced Caregivers',
    description: 'Our team brings years of professional experience combined with ongoing education and certification.',
  },
  {
    icon: Sparkles,
    title: 'Family Peace of Mind',
    description: 'Regular updates, open communication, and transparent care coordination keep families informed and confident.',
  },
];

const highlights = [
  'Licensed & Insured',
  'Background-Checked Caregivers',
  '24/7 Support Available',
  'No Long-Term Contracts',
  'Free Initial Consultation',
  'Family-Owned & Operated',
];

export const WhyChooseSection = () => {
  return (
    <section className="relative py-24 md:py-32 bg-warm-beige overflow-hidden" data-testid="why-choose-section">
      {/* Background Decorations */}
      <div className="absolute top-1/4 left-0 w-[600px] h-[600px] bg-gradient-to-r from-soft-gold/10 to-transparent rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-gradient-to-l from-deep-blue/5 to-transparent rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 md:mb-20"
        >
          <span className="inline-block text-sm font-semibold uppercase tracking-[0.2em] text-soft-gold mb-4">
            Why Families Trust Us
          </span>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-navy mb-6">
            Why Choose <span className="text-gradient-gold">Heritage Home Health LLC</span>
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            For over 15 years, families have trusted Heritage Home Health LLC to provide 
            exceptional care that treats their loved ones like our own.
          </p>
        </motion.div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-20">
          {/* Left - Highlights Card */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="glass-strong shadow-soft-3d-lg rounded-3xl p-8 md:p-10">
              <div className="flex items-center space-x-3 mb-8">
                <div className="w-12 h-12 rounded-full bg-soft-gold/20 flex items-center justify-center">
                  <Award className="w-6 h-6 text-soft-gold" />
                </div>
                <h3 className="font-serif text-2xl text-navy">Our Commitment</h3>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {highlights.map((item, index) => (
                  <motion.div
                    key={item}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1, duration: 0.4 }}
                    className="flex items-center space-x-3"
                  >
                    <div className="w-6 h-6 rounded-full bg-muted-green/20 flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-muted-green" />
                    </div>
                    <span className="text-navy font-medium">{item}</span>
                  </motion.div>
                ))}
              </div>

              <div className="mt-10 pt-8 border-t border-soft-gray">
                <p className="text-gray-600 mb-6">
                  Experience the Heritage Home Health LLC difference. Schedule a free, 
                  no-obligation consultation today.
                </p>
                <Link
                  to="/contact"
                  data-testid="why-choose-cta"
                  className="group inline-flex items-center space-x-2 bg-soft-gold text-navy px-6 py-3 rounded-full text-sm font-semibold hover:bg-opacity-90 transition-all duration-300 shadow-soft-3d hover:shadow-soft-3d-lg hover:-translate-y-1"
                >
                  <span>Get Free Consultation</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>

            {/* Floating Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5, duration: 0.4 }}
              className="absolute -top-6 -right-6 glass shadow-soft-3d rounded-2xl p-4 animate-float"
            >
              <div className="text-center">
                <p className="text-3xl font-bold text-navy">98%</p>
                <p className="text-xs text-gray-500">Client Satisfaction</p>
              </div>
            </motion.div>
          </motion.div>

          {/* Right - Reasons Grid */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="grid grid-cols-1 sm:grid-cols-2 gap-6"
          >
            {reasons.map((reason, index) => {
              const Icon = reason.icon;
              return (
                <motion.div
                  key={reason.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.4 }}
                  className="group p-6 rounded-2xl bg-white shadow-soft-3d hover:shadow-soft-3d-lg transition-all duration-300 hover:-translate-y-1"
                  data-testid={`reason-card-${index}`}
                >
                  <div className="w-10 h-10 rounded-xl bg-soft-gold/10 flex items-center justify-center mb-4 group-hover:bg-soft-gold/20 transition-colors">
                    <Icon className="w-5 h-5 text-soft-gold" />
                  </div>
                  <h4 className="font-serif text-lg text-navy mb-2 group-hover:text-soft-gold transition-colors">
                    {reason.title}
                  </h4>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {reason.description}
                  </p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>

        {/* Stats Banner */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative rounded-3xl bg-gradient-to-r from-navy via-deep-blue to-navy p-8 md:p-12 overflow-hidden"
        >
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-1/4 w-64 h-64 bg-soft-gold rounded-full blur-3xl" />
            <div className="absolute bottom-0 right-1/4 w-48 h-48 bg-white rounded-full blur-3xl" />
          </div>

          <div className="relative grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: '15+', label: 'Years of Service' },
              { value: '5,000+', label: 'Families Served' },
              { value: '200+', label: 'Trained Caregivers' },
              { value: '98%', label: 'Satisfaction Rate' },
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.4 }}
              >
                <p className="font-serif text-4xl md:text-5xl font-medium text-soft-gold mb-2">
                  {stat.value}
                </p>
                <p className="text-white/70 text-sm md:text-base">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyChooseSection;
